import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import toast from 'react-hot-toast';
import { CheckCircle, XCircle, Clock } from 'lucide-react';

export default function AdminWithdrawals() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [selectedAction, setSelectedAction] = useState('');
  const [modalTransactionId, setModalTransactionId] = useState('');
  const [modalNote, setModalNote] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/withdrawal/admin/all?status=${filter}`);
      setWithdrawals(data.withdrawals);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchWithdrawals(); }, [filter]);

  const openActionModal = (withdrawal, action) => {
    setSelectedWithdrawal(withdrawal);
    setSelectedAction(action);
    setModalTransactionId(withdrawal.transactionId || '');
    setModalNote(withdrawal.adminNote || '');
    setModalOpen(true);
  };

  const handleAction = async () => {
    if (!selectedWithdrawal || !selectedAction) return;
    try {
      await axios.put(`/api/withdrawal/admin/${selectedWithdrawal._id}/${selectedAction}`, {
        note: modalNote,
        transactionId: selectedAction === 'approve' ? modalTransactionId : ''
      });
      toast.success(`Withdrawal ${selectedAction}d`);
      setModalOpen(false);
      setSelectedWithdrawal(null);
      setSelectedAction('');
      setModalTransactionId('');
      setModalNote('');
      fetchWithdrawals();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    }
  };

  const statusBadge = { pending: 'bg-amber-500/20 text-amber-300', approved: 'bg-primary-500/20 text-primary-300', rejected: 'bg-red-500/20 text-red-300' };

  return (
    <AdminLayout>
      <div className="animate-fade-in">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Withdrawals</h1>
          <p className="text-gray-400 mt-1">Manage user withdrawal requests</p>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {['pending', 'approved', 'rejected'].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors capitalize
                ${filter === s ? 'bg-primary-600 text-white' : 'bg-dark-700 text-gray-400 hover:text-white border border-white/5'}`}>
              {s === 'pending' && <Clock className="w-3 h-3 inline mr-1" />}
              {s === 'approved' && <CheckCircle className="w-3 h-3 inline mr-1" />}
              {s === 'rejected' && <XCircle className="w-3 h-3 inline mr-1" />}
              {s}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left px-4 py-3 text-gray-400 text-sm">User</th>
                  <th className="text-right px-4 py-3 text-gray-400 text-sm">Amount</th>
                  <th className="text-left px-4 py-3 text-gray-400 text-sm">Wallet</th>
                  <th className="text-left px-4 py-3 text-gray-400 text-sm">Bank Details</th>
                  <th className="text-left px-4 py-3 text-gray-400 text-sm">Date</th>
                  <th className="text-left px-4 py-3 text-gray-400 text-sm">Status</th>
                  {filter === 'pending' && <th className="text-left px-4 py-3 text-gray-400 text-sm">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={filter === 'pending' ? 7 : 6} className="text-center py-12 text-gray-500">Loading...</td></tr>
                ) : withdrawals.length === 0 ? (
                  <tr><td colSpan={filter === 'pending' ? 7 : 6} className="text-center py-12 text-gray-500">No {filter} withdrawals</td></tr>
                ) : withdrawals.map(w => (
                  <tr key={w._id} className="border-b border-white/5 hover:bg-white/2">
                    <td className="px-4 py-3">
                      <p className="text-white text-sm font-medium">{w.user?.name}</p>
                      <p className="text-gray-500 text-xs">{w.user?.email}</p>
                    </td>
                    <td className="px-4 py-3 text-right text-white font-bold">${w.amount.toFixed(2)}</td>
                    <td className="px-4 py-3 text-left text-gray-200 text-sm capitalize">{w.walletType || 'commission'}</td>
                    <td className="px-4 py-3">
                      <p className="text-white text-sm">{w.accountHolderName}</p>
                      <p className="text-gray-400 text-xs">Method: {w.method}</p>
                      {w.method === 'bank' && (
                        <>
                          <p className="text-gray-400 text-xs">{w.bankName} • {w.accountNumber}</p>
                          <p className="text-gray-500 text-xs">IFSC: {w.ifscCode}</p>
                        </>
                      )}
                      {w.method === 'upi' && w.upiId && <p className="text-gray-500 text-xs">UPI: {w.upiId}</p>}
                      {w.method === 'crypto' && w.cryptoAddress && <p className="text-gray-500 text-xs">Address: {w.cryptoAddress} ({w.cryptoNetwork})</p>}
                      {w.transactionId && <p className="text-green-400 text-xs mt-1">Txn ID: {w.transactionId}</p>}
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-sm">{new Date(w.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium ${statusBadge[w.status]}`}>{w.status}</span>
                      {w.adminNote && <p className="text-gray-500 text-xs mt-1">{w.adminNote}</p>}
                    </td>
                    {filter === 'pending' && (
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => openActionModal(w, 'approve')}
                            className="bg-primary-600 hover:bg-primary-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors">
                            <CheckCircle className="w-3 h-3" /> Approve
                          </button>
                          <button onClick={() => openActionModal(w, 'reject')}
                            className="bg-red-600 hover:bg-red-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors">
                            <XCircle className="w-3 h-3" /> Reject
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-lg rounded-3xl bg-dark-800 border border-white/10 p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-white">{selectedAction === 'approve' ? 'Approve Withdrawal' : 'Reject Withdrawal'}</h2>
                <p className="text-gray-400 text-sm mt-1">{selectedAction === 'approve'
                  ? 'Enter optional payment transaction ID and admin note.'
                  : 'Provide a rejection reason for the user.'}</p>
              </div>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-white text-sm">Close</button>
            </div>

            {selectedAction === 'approve' && (
              <div className="mt-6">
                <label className="block text-xs text-gray-400 mb-1.5">Transaction ID (optional)</label>
                <input value={modalTransactionId} onChange={e => setModalTransactionId(e.target.value)}
                  className="w-full bg-dark-700 border border-white/10 rounded-2xl px-4 py-3 text-white focus:outline-none focus:border-primary-500"
                  placeholder="Payment transaction ID" />
              </div>
            )}

            <div className="mt-4">
              <label className="block text-xs text-gray-400 mb-1.5">Admin Note</label>
              <textarea value={modalNote} onChange={e => setModalNote(e.target.value)}
                className="w-full bg-dark-700 border border-white/10 rounded-2xl px-4 py-3 text-white focus:outline-none focus:border-primary-500"
                rows={4}
                placeholder={selectedAction === 'approve' ? 'Optional approval note...' : 'Reason for rejection...'} />
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-2xl bg-dark-700 border border-white/10 text-gray-300 hover:text-white transition-colors">
                Cancel
              </button>
              <button onClick={handleAction}
                className="px-4 py-2 rounded-2xl bg-primary-600 hover:bg-primary-500 text-white transition-colors">
                {selectedAction === 'approve' ? 'Confirm Approve' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
}
